import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAnalyticsStore } from '@/stores/useAnalyticsStore'
import { logger } from './logger'

export function useAnalytics() {
    const location = useLocation()
    const { trackClick, trackPageEntry, trackPageExit, trackMousePosition, trackScrollDepth, isTracking } = useAnalyticsStore()
    const lastPage = useRef<string>(location.pathname)
    const mouseMoveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Track page changes
    useEffect(() => {
        const currentPage = location.pathname

        // Exit previous page
        if (lastPage.current !== currentPage) {
            trackPageExit(lastPage.current)
        }

        // Enter new page
        try {
            trackPageEntry(currentPage)
        } catch (err) {
            logger.captureException(err, { type: 'analytics_page_entry_failed', page: currentPage })
        }
        lastPage.current = currentPage

        // Cleanup on unmount
        return () => {
            trackPageExit(currentPage)
        }
    }, [location.pathname, trackPageEntry, trackPageExit])

    // Track clicks
    useEffect(() => {
        if (!isTracking) return

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            const element = target.tagName.toLowerCase()
            const text = target.textContent?.slice(0, 50) || ''
            const elementId = target.id || ''
            const className = target.className?.toString?.() || ''

            try {
                trackClick({
                    element,
                    text,
                    elementId,
                    className,
                    x: e.clientX,
                    y: e.clientY,
                    page: location.pathname,
                })
            } catch (err) {
                logger.captureException(err, { type: 'analytics_click_track_failed' })
            }
        }

        document.addEventListener('click', handleClick)
        return () => document.removeEventListener('click', handleClick)
    }, [isTracking, location.pathname, trackClick])

    // Track mouse movement (throttled to every 100ms)
    useEffect(() => {
        if (!isTracking) return

        const handleMouseMove = (e: MouseEvent) => {
            if (mouseMoveTimeout.current) return

            mouseMoveTimeout.current = setTimeout(() => {
                trackMousePosition(e.clientX, e.clientY, location.pathname)
                mouseMoveTimeout.current = null
            }, 100)
        }

        document.addEventListener('mousemove', handleMouseMove)
        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            if (mouseMoveTimeout.current) {
                clearTimeout(mouseMoveTimeout.current)
            }
        }
    }, [isTracking, location.pathname, trackMousePosition])

    // Track scroll depth
    useEffect(() => {
        if (!isTracking) return

        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
            const scrolled = window.scrollY
            const depth = scrollHeight > 0 ? Math.round((scrolled / scrollHeight) * 100) : 100

            trackScrollDepth(location.pathname, depth)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [isTracking, location.pathname, trackScrollDepth])
}
